import React, { useEffect, useState } from 'react';
import { Stack } from '@talentmesh/core';
import { useController } from 'react-hook-form';
import CurrencyAutocomplete from './CurrencyAutocomplete';
import YearlySalaryRange from './YearlySalaryRange';
import SalaryError from './SalaryError';
import {
    RecruitmentInformationSalaryExpectation,
    useRecruitmentInfoFormContext,
} from '../../../RecruitmentInformationUtils';
import useTMTranslation from '../../../../../../../Hooks/useTMTranslation';

function Salary(): React.JSX.Element {
    const { tCommon } = useTMTranslation();

    function xor(a: boolean, b: boolean): boolean {
        return (!a && b) || (a && !b);
    }

    const { control } = useRecruitmentInfoFormContext();
    const { fieldState: salaryExpectationMin } = useController({
        name: RecruitmentInformationSalaryExpectation.SalaryExpectationMin,
        control,
    });
    const { fieldState: salaryExpectationMax } = useController({
        name: RecruitmentInformationSalaryExpectation.SalaryExpectationMax,
        control,
    });
    const { fieldState: salaryExpectationCurrency } = useController({
        name: `${RecruitmentInformationSalaryExpectation.SalaryExpectationCurrency}.label`,
        control,
    });

    const [customError, setCustomError] = useState<string | undefined>('');

    useEffect(() => {
        const yearlySalaryFromError = salaryExpectationMin.error?.message;
        const yearlySalaryToError = salaryExpectationMax.error?.message;
        const currencyError = salaryExpectationCurrency.error?.message;

        // xor(a,b) returns true if only one variable is true
        // to check if only one field has error, we use xor(xor(a, b), c)
        // using !! we convert string to boolean
        if (xor(xor(!!yearlySalaryFromError, !!yearlySalaryToError), !!currencyError)) {
            // Only 1 error
            setCustomError(yearlySalaryFromError || yearlySalaryToError || currencyError);
        } else if (yearlySalaryFromError || yearlySalaryToError || currencyError) {
            // More than 1 error
            setCustomError(tCommon('InvalidInputError'));
        } else {
            // No errors
            setCustomError(undefined);
        }
    }, [salaryExpectationMin, salaryExpectationMax, salaryExpectationCurrency]);

    return (
        <Stack>
            <Stack direction="row" spacing={2.5}>
                <YearlySalaryRange />
                <CurrencyAutocomplete />
            </Stack>
            {customError && <SalaryError error={customError} />}
        </Stack>
    );
}

export default Salary;
